import { useRef } from "react";

import classNames from "classnames";
import { Help } from "neetoicons";
import { Button, Popover, Typography } from "neetoui";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import { SIZE } from "./constants";

const HelpPopover = ({
  title,
  size = "large",
  description,
  helpLinkProps,
  popoverProps,
  className,
  iconColor = "currentColor",
  icon: IconComponent = Help,
  ...otherProps
}) => {
  const { t } = useTranslation();

  const popoverRef = useRef();

  return (
    <>
      <Button
        {...otherProps}
        data-testid="help-popover-button"
        icon={() => <IconComponent color={iconColor} size={SIZE[size]} />}
        ref={popoverRef}
        style="text"
        className={classNames(
          "neeto-molecule-help-popover-trigger neeto-ui-rounded-full bg-transparent p-0",
          className
        )}
      />
      <Popover
        appendTo={() => document.body}
        reference={popoverRef}
        {...popoverProps}
      >
        <div
          className="flex flex-col"
          onClick={event => event.stopPropagation()}
        >
          {title && (
            <Popover.Title data-testid="help-popover-title">
              {title}
            </Popover.Title>
          )}
          {typeof description === "string" ? (
            <Typography
              data-testid="help-popover-description"
              lineHeight="normal"
              style="body2"
            >
              {description}
            </Typography>
          ) : (
            description
          )}
          {helpLinkProps && (
            <Button
              className="neeto-ui-mt-3"
              data-testid="help-popover-link-button"
              label={t("neetoMolecules.common.viewHelpArticle")}
              size="small"
              {...helpLinkProps}
              style="link"
              target="_blank"
            />
          )}
        </div>
      </Popover>
    </>
  );
};

HelpPopover.propTypes = {
  /**
   * To set the title of the help popover.
   */
  title: PropTypes.string,
  /**
   * To set the size of the popover help icon.
   */
  size: PropTypes.oneOf(["default", "large"]),
  /**
   * To set the description of the popover, this can be a string or a custom component.
   */
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /**
   * To set the props of the help link which is displayed below the description.
   */
  helpLinkProps: PropTypes.object,
  /**
   * To set the props of the popover to customize it further.
   */
  popoverProps: PropTypes.object,
  /**
   * Additional classes passed on to the help button element.
   */
  className: PropTypes.string,
  /**
   * To set the default icon color of the help icon.
   */
  iconColor: PropTypes.string,
  /**
   * To customize the icon of the help popover.
   */
  icon: PropTypes.node,
};

export default HelpPopover;
